import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

interface CountUpProps {
    value: number
    suffix?: string
    duration?: number
}

// Counts from zero to `value` the first time it scrolls into view.
// The server renders the final number so the figure is never wrong.
export const CountUp: FC<CountUpProps> = ({ value, suffix = '', duration = 1400 }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const [display, setDisplay] = useState(value)

    useEffect(() => {
        const element = ref.current

        if (!element || value === 0) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
        if (typeof IntersectionObserver === 'undefined') return

        let frameId = 0

        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return

            observer.disconnect()

            const startedAt = performance.now()

            const tick = (now: number) => {
                const progress = Math.min(1, (now - startedAt) / duration)
                const eased = 1 - Math.pow(1 - progress, 3)

                setDisplay(Math.round(value * eased))

                if (progress < 1) frameId = requestAnimationFrame(tick)
            }

            setDisplay(0)
            frameId = requestAnimationFrame(tick)
        }, { threshold: 0.4 })

        observer.observe(element)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frameId)
        }
    }, [value, duration])

    return (
        <span ref={ref} className='tabular-nums'>
            {display}{suffix}
        </span>
    )
}
